<template>
  <section class="mt-10">
    <nav
      aria-label="Breadcrumb"
      class="flex"
    >
      <ol class="inline-flex items-center space-x-1 md:space-x-2 rtl:space-x-reverse">
        <li class="inline-flex items-center">
          <a
            class="inline-flex items-center text-sm font-medium text-gray-700 hover:text-primary dark:text-gray-400 dark:hover:text-white"
            href="#"
          >
            Admin
          </a>
        </li>
        <li>
          <div class="flex items-center">
            <svg
              aria-hidden="true"
              class="w-3 h-3 mx-1 text-gray-400 rtl:rotate-180"
              fill="none"
              viewBox="0 0 6 10"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="m1 9 4-4-4-4"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
              />
            </svg>
            <a
              class="text-sm font-medium text-black ms-1 hover:text-primary md:ms-2 dark:text-white dark:hover:text-white"
              href="#"
            >
              Settings
            </a>
          </div>
        </li>
      </ol>
    </nav>

    <div class="mt-6">
      <h2 class="mb-4 text-xl font-semibold leading-none text-gray-900 dark:text-white">
        Site Settings
      </h2>

      <div
        v-if="error"
        class="mb-4 text-red-600"
      >
        {{ error }}
      </div>

      <div
        v-if="loading"
        class="mb-4 text-gray-600"
      >
        Loading...
      </div>

      <form
        v-if="!loading && settings"
        class="space-y-6"
        @submit.prevent="updateSettings"
      >
        <div class="grid gap-4 md:grid-cols-2">
          <div>
            <label
              class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
              for="siteTitle"
            >
              Site Title
            </label>
            <input
              id="siteTitle"
              v-model="settings.siteTitle"
              class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
              placeholder="Enter the site title"
              required
              type="text"
            >
          </div>
          <div>
            <label
              class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
              for="siteDescription"
            >
              Site Description
            </label>
            <input
              id="siteDescription"
              v-model="settings.siteDescription"
              class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
              placeholder="Enter the site description"
              required
              type="text"
            >
          </div>
          <div>
            <label
              class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
              for="metaTitle"
            >
              Meta Title
            </label>
            <input
              id="metaTitle"
              v-model="settings.metaTitle"
              class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
              placeholder="Enter the meta title"
              required
              type="text"
            >
          </div>
          <div>
            <label
              class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
              for="metaDescription"
            >
              Meta Description
            </label>
            <input
              id="metaDescription"
              v-model="settings.metaDescription"
              class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
              placeholder="Enter the meta description"
              required
              type="text"
            >
          </div>
          <div>
            <label
              class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
              for="metaKeywords"
            >
              Meta Keywords
            </label>
            <input
              id="metaKeywords"
              v-model="settings.metaKeywords"
              class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
              placeholder="Enter meta keywords"
              type="text"
            >
          </div>
          <div>
            <label
              class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
              for="contactPhone"
            >
              Contact Phone
            </label>
            <input
              id="contactPhone"
              v-model="settings.contactPhone"
              class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
              placeholder="Enter contact phone"
              type="text"
            >
          </div>
          <div>
            <label
              class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
              for="contactEmail"
            >
              Contact Email
            </label>
            <input
              id="contactEmail"
              v-model="settings.contactEmail"
              class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
              placeholder="Enter contact email"
              type="email"
            >
          </div>
          <div>
            <label
              class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
              for="address"
            >
              Address
            </label>
            <input
              id="address"
              v-model="settings.address"
              class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
              placeholder="Enter the address"
              type="text"
            >
          </div>
        </div>
        <div>
          <h3 class="mt-6 mb-4 text-lg font-medium text-gray-900 dark:text-white">
            Social Media Links
          </h3>
          <div class="grid gap-4 md:grid-cols-3">
            <div>
              <label
                class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                for="facebook"
              >
                Facebook
              </label>
              <input
                id="facebook"
                v-model="settings.socialLinks.facebook"
                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                placeholder="Enter Facebook URL"
                type="text"
              >
            </div>
            <div>
              <label
                class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                for="twitter"
              >
                Twitter
              </label>
              <input
                id="twitter"
                v-model="settings.socialLinks.twitter"
                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                placeholder="Enter Twitter URL"
                type="text"
              >
            </div>
            <div>
              <label
                class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                for="instagram"
              >
                Instagram
              </label>
              <input
                id="instagram"
                v-model="settings.socialLinks.instagram"
                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                placeholder="Enter Instagram URL"
                type="text"
              >
            </div>
          </div>
        </div>

        <button
          class="inline-flex items-center px-5 py-3 text-sm font-medium text-center text-white bg-black rounded-lg hover:bg-gray-800 focus:ring-4 focus:outline-none focus:ring-gray-300 dark:bg-gray-600 dark:hover:bg-gray-700 dark:focus:ring-gray-800"
          type="submit"
        >
          Update Settings
        </button>
      </form>
    </div>
  </section>
</template>

<script lang="ts">
import {defineComponent, inject, ref, watchEffect} from 'vue';
import axios from 'axios';
import {useHead} from '@vueuse/head';
import {settingsKey} from "@/composables/useSettings";

export default defineComponent({
  name: 'Settings',
  setup() {
    const {settings} = inject(settingsKey)!;
    const loading = ref(false);
    const error = ref<string | null>(null);

    watchEffect(() => {
      useHead({
        title: 'Settings',
      });
    });

    const updateSettings = async () => {
      loading.value = true;
      try {
        const response = await axios.put('/settings', settings.value);
        settings.value = response.data;
      } catch (err) {
        console.error('Failed to update settings:', err);
        error.value = 'Failed to update settings.';
      } finally {
        loading.value = false;
      }
    };

    return {settings, loading, error, updateSettings};
  },
});
</script>
